import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'motion/react'
import { useEffect, useId } from 'react'

import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'

export type AstraCircularProgressProps = {
    value: number
    onClick?: () => void
    hideLabel?: boolean
    size?: number
    circleColor?: `stroke-${string}`
    outerCircleColor?: `stroke-${string}`
    noUnstableEffects?: boolean
    className?: string
    'data-test'?: string
}

type CircularProgressContentProps = Pick<AstraCircularProgressProps, 'hideLabel' | 'size' | 'circleColor' | 'outerCircleColor' | 'noUnstableEffects'> & {
    percent: number
}

const CircularProgressContent = ({ percent, hideLabel, size, circleColor, outerCircleColor, noUnstableEffects }: Required<CircularProgressContentProps>) => {
    const center = size / 2
    const pieRadius = Math.floor(size / 3)
    const ringRadius = Math.floor(size / 2.2)
    const clipPathId = useId()
    const shouldReduceMotion = useReducedMotion()
    const rawProgress = useMotionValue(0)
    const progress = useSpring(
        rawProgress,
        shouldReduceMotion || noUnstableEffects
            ? { duration: 0 }
            : {
                  stiffness: 220,
                  damping: 26,
                  mass: 0.6,
              }
    )

    useEffect(() => {
        rawProgress.set(percent)
    }, [percent, rawProgress])

    const dashOffset = useTransform(progress, currentValue => {
        const clampedValue = Math.max(0, Math.min(currentValue, 100))
        return clampedValue <= 0.01 ? 101 : 100 - clampedValue
    })
    const wedgeOpacity = useTransform(progress, [0, 1, 98, 100], [0, 1, 1, 0])
    const fullOpacity = useTransform(progress, [98, 100], [0, 1])

    return (
        <>
            <div className={astraTwMerge('flex h-full items-center justify-center', !hideLabel && 'border-r border-r-strong bg-magic-subtle-rest px-1')}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden="true">
                    <defs>
                        <clipPath id={clipPathId}>
                            <circle cx={center} cy={center} r={pieRadius} />
                        </clipPath>
                    </defs>
                    <circle cx={center} cy={center} r={pieRadius} className="fill-magic-subtle-rest" />
                    <motion.circle
                        cx={center}
                        cy={center}
                        r={pieRadius}
                        fill="none"
                        className={circleColor}
                        strokeWidth={pieRadius * 2}
                        clipPath={`url(#${clipPathId})`}
                        style={{ opacity: fullOpacity }}
                    />
                    <motion.g clipPath={`url(#${clipPathId})`}>
                        <motion.circle
                            cx={center}
                            cy={center}
                            r={pieRadius}
                            fill="none"
                            className={circleColor}
                            strokeWidth={pieRadius * 2}
                            pathLength={100}
                            strokeDasharray={101}
                            style={{ strokeDashoffset: dashOffset, opacity: wedgeOpacity }}
                            transform={`rotate(-90 ${center} ${center})`}
                            strokeLinecap="butt"
                        />
                    </motion.g>
                    <circle cx={center} cy={center} r={ringRadius} fill="none" className={outerCircleColor} strokeWidth={2} />
                </svg>
            </div>
            {!hideLabel && (
                <AstraParagraph weight="strong" variant="body-small" className="w-10 text-center">
                    {percent}%
                </AstraParagraph>
            )}
        </>
    )
}

export const AstraCircularProgress = ({
    value,
    onClick,
    hideLabel = false,
    size = 20,
    circleColor = 'stroke-chart-qualitative-02-primary',
    outerCircleColor = circleColor,
    noUnstableEffects = false,
    className,
    'data-test': dataTest,
}: AstraCircularProgressProps) => {
    const percent = Number.isFinite(value) ? Math.max(0, Math.min(Math.round(value), 100)) : 0
    const content = (
        <CircularProgressContent
            percent={percent}
            hideLabel={hideLabel}
            size={size}
            circleColor={circleColor}
            outerCircleColor={outerCircleColor}
            noUnstableEffects={noUnstableEffects}
        />
    )
    const containerClassName = astraTwMerge(
        'flex items-center overflow-hidden rounded-md bg-surface-base',
        !hideLabel && 'border border-strong',
        onClick && 'cursor-pointer',
        className
    )

    return onClick ? (
        <button type="button" className={containerClassName} onClick={onClick} data-test={dataTest} aria-label={`${percent}% complete`}>
            {content}
        </button>
    ) : (
        <div className={containerClassName} data-test={dataTest} aria-label={`${percent}% complete`} role="img">
            {content}
        </div>
    )
}
